import React, { useState, useEffect, useMemo, useCallback } from "react";
import PropTypes from "prop-types";
import { Responsive, WidthProvider } from "react-grid-layout";
import { map } from "lodash";
import { IoClose, IoSettings } from "react-icons/io5";
import { Tooltip as ReactTooltip } from "react-tooltip";
import { Dropdown, NavItem } from "react-bootstrap";

import HeaderWrapper from "../HeaderWrapper";

import { generateUniqueId } from "../../../utils";

// import { map, range, random } from "lodash";

import "react-grid-layout/css/styles.css";

import "./GridLayout.scss";

// eslint-disable-next-line new-cap
const ResponsiveReactGridLayout = WidthProvider(Responsive);

// const generateLayout = () =>
//   map(range(0, 25), (item, i) => {
//     const y = Math.ceil(Math.random() * 4) + 1;
//     return {
//       x: (random(0, 5) * 2) % 12,
//       y: Math.floor(i / 6) * y,
//       w: 2,
//       h: y,
//       i: i.toString()
//     };
//   });

const GridLayout = ({
  onLayoutChange,
  onDrag,
  onDragStop,
  isDraggable,
  _components,
  _toolbox,
  _layouts,
  heightChange,
  onAddOrRemoveWidget,
  auth,
  isMobile,
  headerActions,
  scrollState
}) => {
  const [state, setState] = useState({
    currentBreakpoint: "lg",
    layouts: {
      lg: []
    },
    toolbox: {
      lg: []
    },
    mounted: false,
    compactType: "vertical"
  });

  useEffect(() => {
    setState((prevState) => ({ ...prevState, mounted: true }));
  }, []);

  useEffect(() => {
    setState((prevState) => ({
      ...prevState,
      toolbox: _toolbox,
      layouts: _layouts
    }));
  }, [_layouts, _toolbox]);

  useEffect(() => {
    const adjustHeight = (height, key) => {
      const itemIndex = state.layouts[state.currentBreakpoint]?.findIndex(
        (l) => l.i === key
      );

      if (itemIndex > -1) {
        const newItem = {
          ...state.layouts[state.currentBreakpoint][itemIndex],
          h: height
        };

        const newLayout = [...state.layouts[state.currentBreakpoint]];
        newLayout.splice(itemIndex, 1, newItem);

        setState((prevState) => ({
          ...prevState,
          layouts: {
            ...prevState.layouts,
            [prevState.currentBreakpoint]: newLayout
          }
        }));
      }
    };
    if (heightChange.changed) {
      adjustHeight(heightChange.height, heightChange.key);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [heightChange]);

  // const onBreakpointChange = breakpoint => {
  //   setState(prevState => ({
  //     ...prevState,
  //     currentBreakpoint: breakpoint,
  //     toolbox: {
  //       ...prevState.toolbox,
  //       [breakpoint]:
  //         prevState.toolbox[breakpoint] ||
  //         prevState.toolbox[prevState.currentBreakpoint] ||
  //         []
  //     }
  //   }));
  // };

  const handleLayoutChange = (layout, layoutsArr) => {
    onLayoutChange(layout, layoutsArr);
    setState((prevState) => ({ ...prevState, layouts: layoutsArr }));
  };

  const onTakeItem = useCallback(
    (item) => {
      setState((prevState) => {
        const newState = {
          ...prevState,
          toolbox: {
            ...prevState.toolbox,
            [prevState.currentBreakpoint]: prevState.toolbox[
              prevState.currentBreakpoint
            ].filter(({ i }) => i !== item.i)
          },
          layouts: {
            ...prevState.layouts,
            [prevState.currentBreakpoint]: [
              ...prevState.layouts[prevState.currentBreakpoint],
              item
            ]
          }
        };
        onAddOrRemoveWidget(newState.layouts);
        return newState;
      });
    },
    [onAddOrRemoveWidget]
  );

  const onPutItem = (item) => {
    setState((prevState) => {
      const newState = {
        ...prevState,
        toolbox: {
          ...prevState.toolbox,
          [prevState.currentBreakpoint]: [
            ...(prevState.toolbox[prevState.currentBreakpoint] || []),
            item
          ]
        },
        layouts: {
          ...prevState.layouts,
          [prevState.currentBreakpoint]: prevState.layouts[
            prevState.currentBreakpoint
          ].filter(({ i }) => i !== item.i)
        }
      };
      onAddOrRemoveWidget(newState.layouts);
      return newState;
    });
  };

  const headerLabel = useMemo(() => {
    const items = _components.filter((component) =>
      _toolbox?.lg?.find((item) => item.i === component.i)
    );
    return (
      <Dropdown>
        <Dropdown.Toggle id="dropdown-custom-components">
          <h1
            className="dashboard-title"
            id="widgets"
            data-tooltip-content="Widgets"
            data-tooltip-variant={auth?.isNightMode ? "dark" : "light"}
            style={{
              color: auth?.isNightMode ? "#FFFFFF" : "#262626"
            }}
          >
            <span style={{ color: auth?.isNightMode ? "#FFFFFF" : "#262626" }}>
              Dashboard
            </span>
            &nbsp;
            <IoSettings style={{ color: "#50accb" }} />
          </h1>
          <ReactTooltip
            anchorId="widgets"
            className={`custom-tooltip ${auth?.isNightMode ? "dark" : "light"}`}
            place="right"
            type={auth?.isNightMode ? "dark" : "light"}
            effect="solid"
            globalEventOff="click"
          />
        </Dropdown.Toggle>

        <Dropdown.Menu>
          {items && items.length ? (
            items.map((item, index) => (
              <NavItem
                key={generateUniqueId()}
                eventKey={index}
                onClick={() => {
                  const toolboxItem = _toolbox?.lg?.find(
                    (ti) => ti.i === item.i
                  );
                  onTakeItem(toolboxItem);
                }}
              >
                <h4 className="menu-option">
                  {item.icon}
                  &nbsp;&nbsp;
                  {item.title}
                </h4>
              </NavItem>
            ))
          ) : (
            <NavItem>
              <div className="menu-option-empty">All Widgets Visible</div>
            </NavItem>
          )}
        </Dropdown.Menu>
      </Dropdown>
    );
  }, [auth, _toolbox, _components, onTakeItem]);

  const generateDOM = () =>
    map(state.layouts[state.currentBreakpoint], (l) => {
      const element = _components.find((c) => c.i === l.i);
      return (
        <div key={l.i}>
          <div
            className="hide-button"
            role="presentation"
            onKeyDown={() => {}}
            onClick={() => onPutItem(l)}
          >
            <IoClose size={18} />
          </div>
          <div style={{ height: element?.style.height }}>
            {element?.component}
          </div>
        </div>
      );
    });

  return (
    <React.Fragment>
      {!isMobile && (
        <HeaderWrapper
          breadcrumbs={[{ label: headerLabel }]}
          fixedActions
          onClickAction={headerActions}
          actionsWidth="auto"
          scrollY={scrollState.scrollY}
        />
      )}
      <div>
        <ResponsiveReactGridLayout
          className="layout"
          rowHeight={45}
          cols={{ lg: 12, md: 10, sm: 6, xs: 4, xxs: 2 }}
          layouts={state.layouts}
          isDraggable={isDraggable}
          // onBreakpointChange={onBreakpointChange}
          onLayoutChange={handleLayoutChange}
          onDrag={onDrag}
          onDragStop={onDragStop}
          measureBeforeMount={false}
          useCSSTransforms={state.mounted}
          compactType={state.compactType}
          preventCollision={!state.compactType}
          isResizable={false}
        >
          {generateDOM()}
        </ResponsiveReactGridLayout>
      </div>
    </React.Fragment>
  );
};

GridLayout.propTypes = {
  onLayoutChange: PropTypes.func,
  onDrag: PropTypes.func,
  isDraggable: PropTypes.bool,
  onDragStop: PropTypes.func,
  heightChange: PropTypes.object,
  _toolbox: PropTypes.object,
  _components: PropTypes.array,
  _layouts: PropTypes.object,
  onAddOrRemoveWidget: PropTypes.func,
  auth: PropTypes.object,
  isMobile: PropTypes.bool,
  headerActions: PropTypes.array,
  scrollState: PropTypes.object
};

GridLayout.defaultProps = {
  onLayoutChange: () => null,
  onDrag: () => null,
  onDragStop: () => null,
  heightChange: {},
  _toolbox: {},
  _components: [],
  _layouts: {},
  onAddOrRemoveWidget: () => null,
  auth: {},
  isMobile: false,
  headerActions: [],
  scrollState: {},
  isDraggable: false
};

export default GridLayout;
